<script setup>
import { ref, computed } from 'vue';

/**
 * Column-header filter / sort menu — the design's LvlHeaderFilter: the header
 * label IS the control (`.lvl-colfilter`), quiet by default, accent when a
 * filter/sort is active, opening the design's `.lvl-menu` card — option rows
 * with slot-rendered labels (e.g. status pills) + counts, an accent check on
 * the selected row(s), and a separated clear row. Single-select closes on
 * pick; multi-select stays open (clear always closes).
 */
const props = defineProps({
  label: { type: String, required: true },
  options: { type: Array, default: () => [] }, // [{ value, label, count? }]
  value: { type: [String, Array], default: null },
  multi: { type: Boolean, default: false },
  active: { type: Boolean, default: false },
  clearLabel: { type: String, default: 'Clear' },
  title: { type: String, default: 'Filter' },
});

const emit = defineEmits(['pick', 'clear']);

const menuOpen = ref(false);

const selected = computed(() => (props.multi ? (Array.isArray(props.value) ? props.value : []) : null));

function isOn(opt) {
  return props.multi ? selected.value.includes(opt.value) : props.value === opt.value;
}

function onPick(opt) {
  emit('pick', opt.value);
  if (!props.multi) menuOpen.value = false;
}

function onClear() {
  emit('clear');
  menuOpen.value = false;
}
</script>

<template>
  <v-menu
    v-model="menuOpen"
    offset-y
    :close-on-content-click="false"
    max-height="420"
    content-class="cc-lvlmenu-content"
  >
    <template v-slot:activator="{ on, attrs }">
      <button
        class="cc-colfilter"
        :class="{ 'is-active': active }"
        :title="title"
        :aria-expanded="menuOpen"
        aria-haspopup="menu"
        v-bind="attrs"
        v-on="on"
      >
        {{ label }}
        <v-icon size="17" class="cc-colfilter-ms">mdi-menu-down</v-icon>
      </button>
    </template>
    <div class="cc-lvlmenu" role="menu">
      <button
        v-for="opt in options"
        :key="String(opt.value)"
        class="cc-lvlmenu-opt"
        :class="{ 'is-on': isOn(opt) }"
        :role="multi ? 'menuitemcheckbox' : 'menuitemradio'"
        :aria-checked="isOn(opt)"
        @click="onPick(opt)"
      >
        <span class="cc-lvlmenu-lbl">
          <slot name="option" :option="opt">{{ opt.label }}</slot>
          <span v-if="opt.count != null" class="cc-lvlmenu-count">({{ opt.count }})</span>
        </span>
        <v-icon v-if="isOn(opt)" size="17" class="cc-lvlmenu-check">mdi-check</v-icon>
      </button>
      <template v-if="active">
        <div class="cc-lvlmenu-sep"></div>
        <button class="cc-lvlmenu-opt cc-lvlmenu-clear" role="menuitem" @click="onClear">
          <v-icon size="16" class="cc-lvlmenu-clear-ms">mdi-close</v-icon>
          {{ clearLabel }}
        </button>
      </template>
    </div>
  </v-menu>
</template>

<style lang="scss" scoped>
/* ---- trigger: the header label IS the filter control (.lvl-colfilter) ---- */
.cc-colfilter {
  display: inline-flex;
  align-items: center;
  gap: 1px;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--cc-text-2, #525c6b);
  padding: 3px 5px 3px 6px;
  margin: -3px -5px -3px -6px;
  border-radius: 7px;
  transition: background 120ms, color 120ms;
  white-space: nowrap;
  min-width: 0;
  overflow: hidden;

  .cc-colfilter-ms {
    color: var(--cc-text-3, #97a0af) !important;
    margin-left: -1px;
  }

  &:hover {
    color: var(--cc-text, #1f242d);
    background: var(--cc-surface-3, rgba(16, 24, 40, 0.055));

    .cc-colfilter-ms {
      color: var(--cc-text-2, #525c6b) !important;
    }
  }

  &:focus-visible {
    outline: 2px solid var(--cc-accent, #1d5bd6);
    outline-offset: 1px;
  }

  &.is-active {
    color: var(--cc-accent, #1d5bd6);

    .cc-colfilter-ms {
      color: var(--cc-accent, #1d5bd6) !important;
    }

    &:hover {
      background: var(--cc-accent-soft, rgba(29, 91, 214, 0.08));
    }
  }
}

/* ---- menu card (.lvl-menu): option rows + counts + check + clear ---- */
.cc-lvlmenu {
  min-width: 188px;
  background: var(--cc-surface, #ffffff);
  border: 1px solid var(--cc-border, #e4e8f2);
  border-radius: 10px;
  padding: 6px;
}

.cc-lvlmenu-opt {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 0;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  color: var(--cc-text, #1f242d);
  text-align: left;
  padding: 9px 10px;
  border-radius: 7px;
  cursor: pointer;

  &:hover {
    background: var(--cc-surface-2, #f9fafe);
  }

  &.is-on {
    color: var(--cc-accent, #1d5bd6);
    font-weight: 650;
  }
}

.cc-lvlmenu-lbl {
  flex: 1 1 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.cc-lvlmenu-count {
  color: var(--cc-text-3, #97a0af);
  font-size: 12px;
}

.cc-lvlmenu-check {
  color: var(--cc-accent, #1d5bd6) !important;
  flex: 0 0 auto;
}

.cc-lvlmenu-sep {
  height: 1px;
  background: var(--cc-border-soft, #edf0f7);
  margin: 5px 6px;
}

.cc-lvlmenu-clear {
  color: var(--cc-text-2, #525c6b);

  .cc-lvlmenu-clear-ms {
    color: var(--cc-text-3, #97a0af) !important;
  }
}
</style>

<!-- The v-menu content wrapper lives in Vuetify's overlay (outside this
     component's scoped tree), so its chrome is neutralised unscoped — the
     inner .cc-lvlmenu carries the design's border/shadow instead. -->
<style lang="scss">
.v-menu__content.cc-lvlmenu-content {
  border-radius: 10px;
  box-shadow: var(--cc-shadow-lg, 0 12px 32px rgba(16, 24, 40, 0.14));
  background: transparent;
}
</style>
